import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SupportForm from './SupportForm';

// ── Live-view helpers ────────────────────────────────────────────────
function parseSquadDateTime(fecha: any, hora: any): Date | null {
    try {
        let year: number, month: number, day: number;
        if (fecha instanceof Date) {
            year = fecha.getUTCFullYear(); month = fecha.getUTCMonth(); day = fecha.getUTCDate();
        } else if (typeof fecha === 'string') {
            const d = new Date(fecha);
            year = d.getUTCFullYear(); month = d.getUTCMonth(); day = d.getUTCDate();
        } else return null;
        let hours = 0, minutes = 0;
        if (typeof hora === 'number') {
            // Firebird TIME: ms since midnight
            hours = Math.floor(hora / 3600000);
            minutes = Math.floor((hora % 3600000) / 60000);
        } else if (typeof hora === 'string') {
            const [h, m] = hora.split(':');
            hours = parseInt(h, 10); minutes = parseInt(m, 10);
        }
        return new Date(year, month, day, hours, minutes, 0);
    } catch { return null; }
}

function parseTournamentDate(fecha: any): Date | null {
    try {
        if (fecha instanceof Date) return new Date(fecha.getUTCFullYear(), fecha.getUTCMonth(), fecha.getUTCDate());
        if (typeof fecha === 'string') { const d = new Date(fecha); return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()); }
        return null;
    } catch { return null; }
}

function getNextLiveSquad(squads: any[], now: Date): any | null {
    const sorted = [...squads].sort((a, b) =>
        (parseSquadDateTime(a.FECHA, a.HORA)?.getTime() || 0) -
        (parseSquadDateTime(b.FECHA, b.HORA)?.getTime() || 0)
    );

    const ADVANCE_WINDOW_MS = 30 * 60 * 1000; // 30 minutes

    // Next upcoming squad (hasn't started yet)
    const upcoming = sorted.find(s => {
        const start = parseSquadDateTime(s.FECHA, s.HORA);
        return start && start.getTime() > now.getTime();
    });

    // If the next upcoming squad starts in ≤30 min, advance to it now
    if (upcoming) {
        const upcomingStart = parseSquadDateTime(upcoming.FECHA, upcoming.HORA)!;
        if (upcomingStart.getTime() - now.getTime() <= ADVANCE_WINDOW_MS) {
            return upcoming;
        }
    }

    // Otherwise show the most recently started squad
    const started = sorted.filter(s => {
        const start = parseSquadDateTime(s.FECHA, s.HORA);
        return start && start.getTime() <= now.getTime();
    });
    if (started.length > 0) return started[started.length - 1];

    // No squad started yet — return first upcoming (live card still shows something)
    return upcoming || null;
}

function formatEventDate(fecha: any): string {
    const d = parseTournamentDate(fecha);
    if (!d) return '';
    return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}
// ─────────────────────────────────────────────────────────────────────

const PlayerDashboard = () => {
    const navigate = useNavigate();
    const [player, setPlayer] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);

    // Live view state
    const [liveViewSquads, setLiveViewSquads] = useState<any[]>([]);

    // Recruiter state
    const [isRecruiter, setIsRecruiter] = useState(false);
    const [recruiterTournaments, setRecruiterTournaments] = useState<any[]>([]);
    const [showRecruiterModal, setShowRecruiterModal] = useState(false);
    const [recruiterStep, setRecruiterStep] = useState<'tournaments' | 'bowler'>('tournaments');
    const [selectedRecruiterTournament, setSelectedRecruiterTournament] = useState<any>(null);
    const [tatInput, setTatInput] = useState('');
    const [bowlerLookupResult, setBowlerLookupResult] = useState<any>(null);
    const [bowlerLookupResults, setBowlerLookupResults] = useState<any[]>([]);
    const [bowlerLookupLoading, setBowlerLookupLoading] = useState(false);
    const [bowlerLookupError, setBowlerLookupError] = useState('');

    useEffect(() => {
        // Get logged in player from localStorage
        const loggedInPlayerStr = localStorage.getItem('loggedInPlayer');
        if (!loggedInPlayerStr) {
            navigate('/login');
            return;
        }

        const loggedInPlayer = JSON.parse(loggedInPlayerStr);
        setPlayer(loggedInPlayer);
        setLoading(false);

        // Check if the player is a recruiter
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        fetch(`${BACKEND_URL}/api/recruiter/status/${loggedInPlayer.CLAVE}`)
            .then(res => res.json())
            .then(data => {
                setIsRecruiter(data.isRecruiter || false);
                setRecruiterTournaments(data.tournaments || []);
            })
            .catch(() => {
                // Non-critical: recruiter features simply won't show
            });
    }, [navigate]);

    // Fetch live view data and poll every 60 s
    useEffect(() => {
        if (!player) return;
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const fetchLiveView = () => {
            fetch(`${BACKEND_URL}/api/my-tournaments/live-view/${player.CLAVE}`)
                .then(res => res.json())
                .then(data => setLiveViewSquads(Array.isArray(data) ? data : []))
                .catch(() => setLiveViewSquads([]));
        };
        fetchLiveView();
        const interval = setInterval(fetchLiveView, 60000);
        return () => clearInterval(interval);
    }, [player]);

    const handleBowlerLookup = async () => {
        const q = tatInput.trim();
        if (!q) return;
        setBowlerLookupLoading(true);
        setBowlerLookupError('');
        setBowlerLookupResult(null);
        setBowlerLookupResults([]);
        try {
            const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
            let url: string;
            if (q.includes('@')) {
                url = `${BACKEND_URL}/api/jugadores?email=${encodeURIComponent(q)}`;
            } else if (/^\d+$/.test(q)) {
                url = `${BACKEND_URL}/api/jugadores?tatid=${encodeURIComponent(q)}`;
            } else {
                url = `${BACKEND_URL}/api/jugadores?name=${encodeURIComponent(q)}`;
            }
            const res = await fetch(url);
            const data = await res.json();
            if (Array.isArray(data) && data.length > 0) {
                if (data.length === 1) {
                    setBowlerLookupResult(data[0]);
                } else {
                    setBowlerLookupResults(data);
                }
            } else {
                setBowlerLookupError('No bowler found. Please check and try again.');
            }
        } catch {
            setBowlerLookupError('Search failed. Please try again.');
        } finally {
            setBowlerLookupLoading(false);
        }
    };

    const resetRecruiterModal = () => {
        setShowRecruiterModal(false);
        setRecruiterStep('tournaments');
        setSelectedRecruiterTournament(null);
        setTatInput('');
        setBowlerLookupResult(null);
        setBowlerLookupResults([]);
        setBowlerLookupError('');
    };

    const handleLogout = () => {
        localStorage.removeItem('loggedInPlayer');
        navigate('/login');
    };

    // Password validation
    const validatePassword = (password: string) => {
        return {
            minLength: password.length >= 8,
            hasUppercase: /[A-Z]/.test(password),
            hasLowercase: /[a-z]/.test(password),
            hasNumber: /[0-9]/.test(password),
            hasSymbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
            noSpaces: !/[\s\t]/.test(password)
        };
    };

    const validation = validatePassword(newPassword);
    const isValidPassword = Object.values(validation).every(v => v);
    const passwordsMatch = newPassword === confirmPassword && confirmPassword !== '';

    // Password strength calculation
    const calculateStrength = (password: string) => {
        let strength = 0;
        if (password.length >= 8) strength += 20;
        if (password.length >= 12) strength += 10;
        if (/[A-Z]/.test(password)) strength += 20;
        if (/[a-z]/.test(password)) strength += 20;
        if (/[0-9]/.test(password)) strength += 15;
        if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) strength += 15;
        return Math.min(strength, 100);
    };

    const passwordStrength = calculateStrength(newPassword);
    const getStrengthLabel = () => {
        if (passwordStrength === 0) return '';
        if (passwordStrength < 40) return 'Weak';
        if (passwordStrength < 70) return 'Fair';
        if (passwordStrength < 90) return 'Good';
        return 'Strong';
    };

    const getStrengthColor = () => {
        if (passwordStrength < 40) return 'bg-red-500';
        if (passwordStrength < 70) return 'bg-yellow-500';
        if (passwordStrength < 90) return 'bg-blue-500';
        return 'bg-green-500';
    };

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isValidPassword || !passwordsMatch || !currentPassword) return;

        setPasswordError('');
        setPasswordLoading(true);

        try {
            const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
            const response = await fetch(`${BACKEND_URL}/api/auth/update-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: player.EMAIL,
                    currentPassword,
                    newPassword
                })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                // Success - show animation
                setShowSuccess(true);

                // Auto-close after 2 seconds
                setTimeout(() => {
                    resetPasswordModal();
                }, 2000);
            } else {
                // Error - show error message
                setPasswordError(data.error || 'Failed to update password');
            }
        } catch (err) {
            console.error('Password update error:', err);
            setPasswordError('An error occurred. Please try again.');
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleForgotPasswordFromModal = async () => {
        if (!player?.EMAIL) return;

        try {
            const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
            await fetch(`${BACKEND_URL}/api/auth/request-password-reset`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: player.EMAIL })
            });

            // Close modal and show alert
            resetPasswordModal();
            alert('Password reset email sent! Please check your inbox.');
        } catch (err) {
            console.error('Forgot password error:', err);
            alert('Failed to send reset email. Please try again.');
        }
    };

    const resetPasswordModal = () => {
        setShowPasswordModal(false);
        setShowSuccess(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading your dashboard...</p>
                </div>
            </div>
        );
    }

    if (!player) return null;

    return (
        <>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
            {/* Header with User Info */}
            <div className="bg-white shadow-2xl border-b-4 border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="bg-gray-50 rounded-2xl p-3 shadow-inner">
                                <img 
                                    src="/logo.avif" 
                                    alt="Logo" 
                                    className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
                                />
                            </div>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{player.NOMBRECOMPLETO}</h1>
                                <p className="text-gray-600 text-sm sm:text-base">TAT ID: {player.FICHANAL}</p>
                                <p className="text-gray-600 text-sm sm:text-base">{player.EMAIL} | {player.CITY}, {player.ESTADOCORTO}</p>
                                {isRecruiter && (
                                    <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-700 border border-purple-300">
                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                                        </svg>
                                        Recruiter
                                    </span>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="self-start sm:self-auto bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition flex items-center gap-2 shadow-lg"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            Logout
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Action Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {/* Register to Tournament */}
                    <button
                        onClick={() => navigate('/welcome', { state: { fromDashboard: true } })}
                        className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition transform hover:-translate-y-1 text-left group"
                    >
                        <div className="bg-blue-100 rounded-xl p-3 inline-block mb-4 group-hover:scale-110 transition">
                            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">Register to a Tournament</h3>
                        <p className="text-gray-600 text-sm">Sign up for upcoming bowling tournaments</p>
                    </button>

                    {/* View My Tournaments */}
                    <button
                        onClick={() => navigate('/my-tournaments')}
                        className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition transform hover:-translate-y-1 text-left group"
                    >
                        <div className="bg-green-100 rounded-xl p-3 inline-block mb-4 group-hover:scale-110 transition">
                            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">My Registered Tournaments</h3>
                        <p className="text-gray-600 text-sm">View tournaments you're registered for</p>
                    </button>

                    {/* View All Tournament Results */}
                    <button
                        onClick={() => navigate('/all-tournament-results')}
                        className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition transform hover:-translate-y-1 text-left group"
                    >
                        <div className="bg-purple-100 rounded-xl p-3 inline-block mb-4 group-hover:scale-110 transition">
                            <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">View All Tournament Results</h3>
                        <p className="text-gray-600 text-sm">Browse results from all tournaments</p>
                    </button>

                    {/* Security */}
                    <button
                        onClick={() => setShowPasswordModal(true)}
                        className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition transform hover:-translate-y-1 text-left group"
                    >
                        <div className="bg-red-100 rounded-xl p-3 inline-block mb-4 group-hover:scale-110 transition">
                            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">Security</h3>
                        <p className="text-gray-600 text-sm">Manage your account security settings by updating your password</p>
                    </button>

                    {/* Register Bowlers (Recruiter only) */}
                    {isRecruiter && (
                        <button
                            onClick={() => setShowRecruiterModal(true)}
                            className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition transform hover:-translate-y-1 text-left group border-2 border-purple-100"
                        >
                            <div className="bg-purple-100 rounded-xl p-3 inline-block mb-4 group-hover:scale-110 transition">
                                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">Register Your Bowlers</h3>
                            <p className="text-gray-600 text-sm">Register bowlers to your assigned tournaments</p>
                        </button>
                    )}

                    {/* Recruiter Roster card — visible only to recruiters */}
                    {isRecruiter && (
                        <button
                            onClick={() => navigate('/recruiter/roster')}
                            className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition transform hover:-translate-y-1 text-left group border-2 border-purple-100"
                        >
                            <div className="bg-purple-100 rounded-xl p-3 inline-block mb-4 group-hover:scale-110 transition">
                                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">My Roster</h3>
                            <p className="text-gray-600 text-sm">View bowlers you've registered and fee status</p>
                        </button>
                    )}

                    {/* Make Teams */}
                    <button
                        onClick={() => navigate('/make-teams')}
                        className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition transform hover:-translate-y-1 text-left group"
                    >
                        <div className="bg-yellow-100 rounded-xl p-3 inline-block mb-4 group-hover:scale-110 transition">
                            
                                <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 12h6m-6 4h6" />
                                </svg>
                            
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">Make Teams</h3>
                        <p className="text-gray-600 text-sm">Create your team for your registered team events</p>
                    </button>

                    {/* My Tournament Balances */}
                    <button
                        onClick={() => navigate('/my-finances')}
                        className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition transform hover:-translate-y-1 text-left group"
                    >
                        <div className="bg-pink-100 rounded-xl p-3 inline-block mb-4 group-hover:scale-110 transition">
                            <svg className="w-8 h-8 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">My Tournament Balances</h3>
                        <p className="text-gray-600 text-sm">View your tournament balances and payments</p>
                    </button>
                </div>

                {/* ── Live Tournament Section ─────────────────────────────── */}
                {(() => {
                    if (!liveViewSquads.length) return null;

                    const now = new Date();
                    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

                    // Build ordered list of unique tournaments (already sorted by FECHAINICIO ASC)
                    const seenKeys = new Set<number>();
                    const tourneyGroups: any[][] = [];
                    for (const s of liveViewSquads) {
                        if (!seenKeys.has(s.TORNEO_CLAVE)) {
                            seenKeys.add(s.TORNEO_CLAVE);
                            tourneyGroups.push(liveViewSquads.filter(x => x.TORNEO_CLAVE === s.TORNEO_CLAVE));
                        }
                    }

                    let info: { squads: any[]; isActive: boolean; liveSquad: any } | null = null;
                    for (const squads of tourneyGroups) {
                        const startDate = parseTournamentDate(squads[0].FECHAINICIO);
                        const endDate   = parseTournamentDate(squads[0].FECHAFIN);
                        if (!startDate || !endDate) continue;

                        const isActive = today >= startDate && today <= endDate;
                        if (isActive) {
                            const liveSquad = getNextLiveSquad(squads, now);
                            if (liveSquad) { info = { squads, isActive: true, liveSquad }; break; }
                        } else if (today < startDate) {
                            info = { squads, isActive: false, liveSquad: squads[0] }; break;
                        }
                    }
                    if (!info) return null;

                    const { squads, isActive, liveSquad } = info;
                    const hasLane =
                        liveSquad.MESA && String(liveSquad.MESA) !== '0' &&
                        liveSquad.LETRA && String(liveSquad.LETRA) !== '0';

                    return (
                        <div className="mb-10">
                            {/* Section header */}
                            <div className="flex items-center gap-2 mb-4">
                                {isActive ? (
                                    <>
                                        <span className="flex h-3 w-3 relative">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                                        </span>
                                        <h2 className="text-sm font-bold text-red-600 uppercase tracking-widest">Live</h2>
                                        <span className="text-gray-300 mx-1">•</span>
                                        <h2 className="text-sm font-bold text-gray-500 uppercase tracking-widest">{squads[0].TORNEO_NOMBRE}</h2>
                                    </>
                                ) : (
                                    <>
                                        <div className="bg-blue-100 rounded-lg p-1.5 flex-shrink-0">
                                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                        <h2 className="text-sm font-bold text-gray-500 uppercase tracking-widest">Your Next Event</h2>
                                    </>
                                )}
                            </div>

                            {/* ── UPCOMING card ─────────────────────── */}
                            {!isActive && (
                                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5">
                                        <div className="flex items-center gap-4">
                                            <div className="bg-white/20 rounded-xl p-2.5 flex-shrink-0">
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="text-blue-100 text-xs font-semibold uppercase tracking-wider mb-0.5">Your Next Tournament</p>
                                                <h3 className="text-white font-bold text-xl leading-tight">{squads[0].TORNEO_NOMBRE}</h3>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="px-6 py-5">
                                        <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-5">
                                            {squads[0].NOMBREBOLICHE && (
                                                <span className="flex items-center gap-1.5">
                                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    </svg>
                                                    {squads[0].NOMBREBOLICHE}{squads[0].CIUDADBOLICHE ? ` • ${squads[0].CIUDADBOLICHE}` : ''}
                                                </span>
                                            )}
                                            <span className="flex items-center gap-1.5">
                                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                                Starts {formatEventDate(squads[0].FECHAINICIO)}
                                            </span>
                                        </div>
                                        <div className="border-t border-gray-100 pt-4">
                                            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Your First Registered Squad</p>
                                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                                                <p className="font-bold text-gray-800 text-lg">{liveSquad.DIASEMANA_I} &nbsp;·&nbsp; {liveSquad.HORARIO_I}</p>
                                                <p className="text-blue-600 font-semibold text-sm mt-1">{liveSquad.EVENTNAME}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── LIVE card ─────────────────────────── */}
                            {isActive && (
                                <div
                                    className="rounded-2xl shadow-2xl overflow-hidden border border-indigo-500/20"
                                    style={{ background: 'linear-gradient(135deg, rgba(15,23,42,0.80) 0%, rgba(30,27,75,0.85) 50%, rgba(15,23,42,0.60) 100%)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)' }}
                                >
                                    {/* Top bar */}
                                    <div className="px-6 py-3 border-b border-white/10 flex items-center gap-3">
                                        <span className="flex h-2.5 w-2.5 relative">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                                        </span>
                                        <span className="text-red-400 text-xs font-bold uppercase tracking-widest">On Air</span>
                                        {squads[0].NOMBREBOLICHE && (
                                            <>
                                                <span className="text-indigo-300/30 mx-1">|</span>
                                                <span className="text-gray-300 text-xs">{squads[0].NOMBREBOLICHE}</span>
                                            </>
                                        )}
                                    </div>

                                    <div className="px-6 py-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        {/* Squad info */}
                                        <div>
                                            <p className="text-indigo-300 text-xs font-bold uppercase tracking-widest mb-3">Your Next Squad</p>
                                            <p className="text-white font-extrabold text-3xl leading-tight">{liveSquad.DIASEMANA_I}</p>
                                            <p className="text-indigo-200 font-semibold text-xl mt-0.5">{liveSquad.HORARIO_I}</p>
                                            <p className="text-gray-300 text-sm mt-2">{liveSquad.EVENTNAME}</p>
                                        </div>

                                        {/* Lane assignment */}
                                        <div className="sm:border-l sm:border-white/10 sm:pl-6">
                                            <p className="text-indigo-300 text-xs font-bold uppercase tracking-widest mb-3">Lane Assignment </p>
                                            
                                            {!hasLane ? (
                                                <div className="bg-white/5 rounded-xl p-4 border border-indigo-400/20">
                                                    <div className="flex items-start gap-2">
                                                        <svg className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        <p className="text-white font-bold text-xs leading-relaxed">
                                                            Lane assignment will appear here. Please be at the front desk <span className="text-yellow-300 font-semibold">15 minutes before</span> the start of the event to draw a lane.
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex items-end gap-8">
                                                    <div className="text-center">
                                                        <p className="text-indigo-300 text-xs uppercase tracking-widest mb-1">Lane</p>
                                                        <p className="text-white font-black text-6xl leading-none tracking-tight">{liveSquad.MESA}</p>
                                                    </div>
                                                    <div className="text-center">
                                                        <p className="text-indigo-300 text-xs uppercase tracking-widest mb-1">Letter</p>
                                                        <p className="text-white font-black text-6xl leading-none tracking-tight">{liveSquad.LETRA}</p>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="flex items-center gap-2 mt-3 animate-pulse">
                                                <span className="flex h-2 w-2 relative flex-shrink-0">
                                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-400"></span>
                                                </span>
                                                <p className="text-red-400 uppercase text-xs font-semibold tracking-wide">
                                                    View your scores{' '}
                                                    <button
                                                        onClick={() => navigate('/my-tournaments')}
                                                        className="underline uppercase hover:text-red-300 transition-colors"
                                                    >
                                                        here
                                                    </button>
                                                    {' '}once they become available.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Recruiter Tutorial — visible only to recruiters */}
                {isRecruiter && (
                    <div className="mt-10">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="bg-red-100 rounded-lg p-1.5 flex-shrink-0">
                                <svg className="w-4 h-4 text-red-600" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                                </svg>
                            </div>
                            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-widest">Recruiter Tutorial</h2>
                        </div>
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-100">
                                <p className="text-base font-semibold text-gray-800">How to Register Your Bowlers</p>
                                <p className="text-sm text-gray-400 mt-0.5">Watch this quick guide to get started as a recruiter</p>
                            </div>
                            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                                <iframe
                                    className="absolute inset-0 w-full h-full"
                                    src="https://www.youtube.com/embed/EFMW5E_fbJI"
                                    title="Recruiter Tutorial"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Recruiter Modal */}
            {showRecruiterModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full my-auto flex flex-col" style={{ maxHeight: 'calc(100vh - 60px)' }}>
                        {/* Modal Header */}
                        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-5 rounded-t-3xl">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    {recruiterStep === 'bowler' && (
                                        <button
                                            onClick={() => { setRecruiterStep('tournaments'); setBowlerLookupResult(null); setBowlerLookupResults([]); setBowlerLookupError(''); setTatInput(''); }}
                                            className="text-gray-500 hover:text-gray-700 transition"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                            </svg>
                                        </button>
                                    )}
                                    <h2 className="text-xl font-bold text-gray-900">
                                        {recruiterStep === 'tournaments'
                                            ? 'Select Tournament'
                                            : `Register Bowler — ${selectedRecruiterTournament?.nombre}`}
                                    </h2>
                                </div>
                                <button onClick={resetRecruiterModal} className="text-gray-400 hover:text-gray-600 transition">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div className="flex-1 overflow-y-auto px-6 py-6">
                            {recruiterStep === 'tournaments' && (
                                <div className="space-y-3">
                                    {recruiterTournaments.length === 0 ? (
                                        <p className="text-gray-500 text-center py-8">No active tournaments assigned to you.</p>
                                    ) : (
                                        recruiterTournaments.map(t => (
                                            <button
                                                key={t.clave}
                                                onClick={() => { setSelectedRecruiterTournament(t); setRecruiterStep('bowler'); }}
                                                className="w-full text-left bg-white border-2 border-indigo-200 rounded-2xl p-4 hover:border-indigo-500 hover:bg-indigo-50 transition"
                                            >
                                                <div className="font-bold text-gray-900">{t.nombre}</div>
                                                <div className="text-sm text-gray-500 mt-1">
                                                    {new Date(t.fechaInicio).toLocaleDateString()}
                                                    {t.fechaTermino && ` — ${new Date(t.fechaTermino).toLocaleDateString()}`}
                                                </div>
                                            </button>
                                        ))
                                    )}
                                </div>
                            )}

                            {recruiterStep === 'bowler' && (
                                <div>
                                    <p className="text-gray-600 mb-4 text-sm">Search by <span className="font-semibold text-gray-700">TAT#</span>, <span className="font-semibold text-gray-700">first or last name</span>, or <span className="font-semibold text-gray-700">email</span>.</p>
                                    <div className="flex gap-2 mb-4">
                                        <input
                                            type="text"
                                            placeholder="TAT#, first or last name, or email…"
                                            value={tatInput}
                                            onChange={e => { setTatInput(e.target.value); setBowlerLookupError(''); setBowlerLookupResult(null); setBowlerLookupResults([]); }}
                                            onKeyDown={e => e.key === 'Enter' && handleBowlerLookup()}
                                            className="flex-1 border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
                                        />
                                        <button
                                            onClick={handleBowlerLookup}
                                            disabled={bowlerLookupLoading || !tatInput.trim()}
                                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-3 rounded-xl font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {bowlerLookupLoading ? 'Searching...' : 'Find'}
                                        </button>
                                    </div>

                                    {bowlerLookupError && (
                                        <div className="text-red-600 text-sm mb-4 flex items-center gap-2">
                                            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                            </svg>
                                            {bowlerLookupError}
                                        </div>
                                    )}

                                    {bowlerLookupResults.length > 1 && (
                                        <div className="space-y-2 mb-4">
                                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">{bowlerLookupResults.length} results — pick one</p>
                                            {bowlerLookupResults.map(b => (
                                                <button
                                                    key={b.CLAVE}
                                                    onClick={() => { setBowlerLookupResult(b); setBowlerLookupResults([]); }}
                                                    className="w-full text-left border-2 border-gray-200 hover:border-indigo-400 hover:bg-indigo-50 rounded-xl px-4 py-3 transition"
                                                >
                                                    <p className="font-bold text-gray-900 text-sm">{b.NOMBRECOMPLETO}</p>
                                                    <p className="text-xs text-gray-500 mt-0.5">TAT# {b.FICHANAL} &nbsp;·&nbsp; {b.EMAIL}</p>
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {bowlerLookupResult && (
                                        <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-5">
                                            <div className="font-bold text-gray-900 text-lg">{bowlerLookupResult.NOMBRECOMPLETO}</div>
                                            <div className="text-sm text-gray-600 mt-1">TAT#: {bowlerLookupResult.FICHANAL}</div>
                                            <div className="text-sm text-gray-600">{bowlerLookupResult.EMAIL}</div>
                                            <div className="text-sm text-gray-500">{bowlerLookupResult.CITY}, {bowlerLookupResult.ESTADOCORTO}</div>
                                            <button
                                                onClick={() => {
                                                    navigate('/turnos-signup', {
                                                        state: {
                                                            jugador: bowlerLookupResult,
                                                            tournament: selectedRecruiterTournament,
                                                            recruiterMode: true,
                                                        }
                                                    });
                                                    resetRecruiterModal();
                                                }}
                                                className="mt-4 w-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-xl font-bold transition flex items-center justify-center gap-2"
                                            >
                                                Register {bowlerLookupResult.NOMBRECOMPLETO}
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                                </svg>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Password Update Modal */}
            {showPasswordModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full my-auto flex flex-col" style={{ maxHeight: 'calc(100vh - 100px)' }}>
                        {!showSuccess ? (
                            <>
                                {/* Modal Header */}
                                <div className="flex-shrink-0 bg-white border-b border-gray-200 px-8 py-6 rounded-t-3xl">
                                    <div className="flex items-center justify-between">
                                        <h2 className="text-2xl font-bold text-gray-900">Update your password</h2>
                                        <button
                                            onClick={resetPasswordModal}
                                            className="text-gray-400 hover:text-gray-600 transition"
                                        >
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                {/* Modal Body */}
                                <form onSubmit={handlePasswordSubmit} className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
                                    {/* Current Password */}
                                    <div>
                                        <label htmlFor="currentPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                                            Current Password
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showCurrentPassword ? "text" : "password"}
                                                id="currentPassword"
                                                value={currentPassword}
                                                onChange={(e) => setCurrentPassword(e.target.value)}
                                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all pr-12"
                                                placeholder="Enter your current password"
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                            >
                                                {showCurrentPassword ? (
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                                    </svg>
                                                ) : (
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {/* New Password */}
                                    <div>
                                        <label htmlFor="newPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                                            New Password
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showNewPassword ? "text" : "password"}
                                                id="newPassword"
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all pr-12"
                                                placeholder="Enter your new password"
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                            >
                                                {showNewPassword ? (
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                                    </svg>
                                                ) : (
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                    </svg>
                                                )}
                                            </button>
                                        </div>

                                        {/* Password Strength Meter */}
                                        {newPassword && (
                                            <div className="mt-3">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-xs font-medium text-gray-600">Password Strength</span>
                                                    <span className={`text-xs font-bold ${
                                                        passwordStrength < 40 ? 'text-red-600' :
                                                        passwordStrength < 70 ? 'text-yellow-600' :
                                                        passwordStrength < 90 ? 'text-blue-600' :
                                                        'text-green-600'
                                                    }`}>
                                                        {getStrengthLabel()}
                                                    </span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                                    <div
                                                        className={`h-full transition-all duration-300 ${getStrengthColor()}`}
                                                        style={{ width: `${passwordStrength}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Validation Checklist */}
                                        {newPassword && (
                                            <div className="mt-4 space-y-2 bg-gray-50 rounded-xl p-4">
                                                <p className="text-xs font-semibold text-gray-700 mb-2">Password must contain:</p>
                                                <div className="space-y-1.5">
                                                    <div className="flex items-center gap-2">
                                                        {validation.minLength ? (
                                                            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        ) : (
                                                            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                        <span className={`text-xs ${validation.minLength ? 'text-green-700 font-medium' : 'text-gray-600'}`}>
                                                            At least 8 characters
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {validation.hasUppercase ? (
                                                            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        ) : (
                                                            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                        <span className={`text-xs ${validation.hasUppercase ? 'text-green-700 font-medium' : 'text-gray-600'}`}>
                                                            At least one uppercase letter
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {validation.hasLowercase ? (
                                                            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        ) : (
                                                            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                        <span className={`text-xs ${validation.hasLowercase ? 'text-green-700 font-medium' : 'text-gray-600'}`}>
                                                            At least one lowercase letter
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {validation.hasNumber ? (
                                                            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        ) : (
                                                            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                        <span className={`text-xs ${validation.hasNumber ? 'text-green-700 font-medium' : 'text-gray-600'}`}>
                                                            At least one number
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {validation.hasSymbol ? (
                                                            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        ) : (
                                                            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                        <span className={`text-xs ${validation.hasSymbol ? 'text-green-700 font-medium' : 'text-gray-600'}`}>
                                                            At least one symbol (!@#$%^&*...)
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {validation.noSpaces ? (
                                                            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        ) : (
                                                            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                        <span className={`text-xs ${validation.noSpaces ? 'text-green-700 font-medium' : 'text-gray-600'}`}>
                                                            No spaces or tabs
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Confirm Password */}
                                    <div>
                                        <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                                            Confirm New Password
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                id="confirmPassword"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all pr-12"
                                                placeholder="Confirm your new password"
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                            >
                                                {showConfirmPassword ? (
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                                    </svg>
                                                ) : (
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                        {confirmPassword && !passwordsMatch && (
                                            <p className="text-red-600 text-xs mt-2 flex items-center gap-1">
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                                Passwords do not match
                                            </p>
                                        )}
                                        {confirmPassword && passwordsMatch && (
                                            <p className="text-green-600 text-xs mt-2 flex items-center gap-1">
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                </svg>
                                                Passwords match
                                            </p>
                                        )}
                                    </div>

                                    {/* Error Message */}
                                    {passwordError && (
                                        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                                            <div className="flex items-start gap-2">
                                                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                                <div>
                                                    <p className="font-medium">{passwordError}</p>
                                                    {passwordError.includes('Current password') && (
                                                        <p className="mt-1">
                                                            <button
                                                                type="button"
                                                                onClick={handleForgotPasswordFromModal}
                                                                className="text-red-900 underline hover:text-red-700 font-semibold"
                                                            >
                                                                Forgot password?
                                                            </button>
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Modal Footer */}
                                    <div className="flex gap-3 pt-4">
                                        <button
                                            type="button"
                                            onClick={resetPasswordModal}
                                            className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={!isValidPassword || !passwordsMatch || !currentPassword || passwordLoading}
                                            className={`flex-1 px-6 py-3 rounded-xl font-semibold transition ${
                                                isValidPassword && passwordsMatch && currentPassword && !passwordLoading
                                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            }`}
                                        >
                                            {passwordLoading ? (
                                                <span className="flex items-center justify-center gap-2">
                                                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Updating...
                                                </span>
                                            ) : (
                                                'Update Password'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </>
                        ) : (
                            /* Success State */
                            <div className="px-8 py-16 text-center">
                                <div className="mb-6 flex justify-center">
                                    <div className="bg-green-100 rounded-full p-4 animate-bounce">
                                        <svg className="w-16 h-16 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">Password updated successfully!</h3>
                                <p className="text-gray-600">Your account is now more secure.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>

        {/* Floating Support Button */}
        {player && <SupportForm player={player} />}
    </>
    );
};

export default PlayerDashboard;
